import React, { useState } from "react";
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid2";

// Create theme
const theme = createTheme({
  typography: {
    fontFamily: ["Tajawal-Regular", "Tajawal-Bold"].join(","),
  },
});

// Password validation schema
const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .matches(/[a-zA-Z]/, "Password must contain at least one letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Please confirm your new password"),
});

export default function UpdatePassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
          }}
        >
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <Grid
                container
                spacing={2}
                sx={{
                  padding: { xs: 1, sm: 3 },
                  borderRadius: 2,
                }}
              >
                <Grid item size={{ xs: 12 }} sx={{ marginTop: "-14%" }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "Tajawal-Bold",
                      fontWeight: 700,
                      textAlign: "center",
                      color: "#121212",
                    }}
                  >
                    Update Password
                  </Typography>
                </Grid>

                {/* Current Password */}
                <Grid item size={{ xs: 12 }} sx={{ marginTop: "-8%" }}>
                  <Typography
                    sx={{
                      fontFamily: "Tajawal-Regular",
                      color: "#121212",
                      fontSize: "20px",
                      fontWeight: 500,
                    }}
                  >
                    Current Password
                  </Typography>
                  <TextField
                    fullWidth
                    type="password"
                    name="currentPassword"
                    placeholder="Type your current password"
                    value={values.currentPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.currentPassword && Boolean(errors.currentPassword)
                    }
                    helperText={
                      touched.currentPassword && errors.currentPassword
                    }
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", // Apply border radius
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on focus
                        },
                      },
                      "& .MuiInputLabel-root": {
                        "&.Mui-focused": {
                          color: "#3173B1", // Change label color on focus
                        },
                      },
                    }}
                  />
                </Grid>

                {/* New Password */}
                <Grid item size={{ xs: 12 }} mt={1}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "Tajawal-Regular",
                      color: "#121212",
                      fontSize: "20px",
                      fontWeight: 500,
                    }}
                  >
                    New Password
                  </Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="Type your new password"
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.newPassword && Boolean(errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePasswordVisibility}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", // Apply border radius
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on focus
                        },
                      },
                      "& .MuiInputLabel-root": {
                        "&.Mui-focused": {
                          color: "#3173B1", // Change label color on focus
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Confirm New Password */}
                <Grid item size={{ xs: 12 }} mt={1}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "Tajawal-Regular",
                      color: "#121212",
                      fontSize: "20px",
                      fontWeight: 500,
                    }}
                  >
                    Confirm New Password
                  </Typography>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm your new password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.confirmPassword && Boolean(errors.confirmPassword)
                    }
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                    }
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleConfirmPasswordVisibility}
                          >
                            {showConfirmPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", // Apply border radius
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on hover
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#3173B1", // Apply border color on focus
                        },
                      },
                      "& .MuiInputLabel-root": {
                        "&.Mui-focused": {
                          color: "#3173B1", // Change label color on focus
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item size={{ xs: 12 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    sx={{
                      background:
                        "linear-gradient(90deg, #004D8A 0%, #02599C 20.9%, #0886E1 73.48%, #0A91F2 97.3%)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "20px",
                      fontFamily: "Tajawal-Bold",
                      padding: "16px 0",
                      borderTopLeftRadius: "15px",
                      borderBottomRightRadius: "15px",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #02599C 0%, #004D8A 20.9%, #0886E1 73.48%, #0A91F2 97.3%)",
                      },
                    }}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </ThemeProvider>
  );
}
